import { useCallback, useMemo } from "react";

import { isDev } from "@/helpers/environment/environment.helpers";
import { useTracker } from "@/hooks/useTracker/useTracker";
import { useLocation } from "react-router-dom";
import { useGetFeaturesFlags } from "../useGetFeaturesFlags/useGetFeaturesFlags";
import { BasePayload, EventPayloadMapper, TrackArgs } from "./useTrack.decl";

function pickLocation(pathname: string): BasePayload["location"] {
  switch (true) {
    case pathname.includes("conversations"):
      return "conversation_center";
    case /calls\/\d+\/\w+/.test(pathname):
      return "assets_page";
    case pathname.includes("trackers"):
      return "trackers";
    default:
      return pathname;
  }
}

/* istanbul ignore next */
function fakeTrack<
  TrackEvent extends keyof EventPayloadMapper = keyof EventPayloadMapper,
>(...args: TrackArgs<TrackEvent>) {
  const [trackEvent, payload] = args;

  if (!payload) {
    console.log(
      `🔍 %c[TRACK]%c => event %c"${trackEvent}".`,
      "color: #ff80bf;",
      "color: initial;",
      "color: #ff80bf;"
    );
    return;
  }

  console.log(
    `🔍 %c[TRACK]%c => event %c"${trackEvent}"%c${
      payload && ` with payload %c"${JSON.stringify(payload, null, 2)}"`
    }.`,
    "color: #ff80bf;",
    "color: initial;",
    "color: #ff80bf;",
    "color: initial;",
    "color: #8aff80;"
  );
}

export function useTrack() {
  const { track } = useTracker();
  const featureFlags = useGetFeaturesFlags();
  const { pathname } = useLocation();

  const basePayload: BasePayload = useMemo(
    () => ({
      location: pickLocation(pathname),
    }),
    [pathname]
  );

  const enhancedTrack = useCallback(
    <TrackEvent extends keyof EventPayloadMapper = keyof EventPayloadMapper>(
      ...args: TrackArgs<TrackEvent>
    ): void => {
      const [trackEvent, payload] = args;

      track({ event: trackEvent, payload: { ...basePayload, ...payload } });
    },
    [basePayload, track]
  );

  const useTrackReturn = useMemo(
    () => ({
      track:
        featureFlags.disableFrontendTracking || isDev()
          ? fakeTrack
          : enhancedTrack,
    }),
    [featureFlags.disableFrontendTracking, enhancedTrack]
  );

  return useTrackReturn;
}
